import { x } from '@xstyled/styled-components';
import React from 'react';
import { t } from 'ttag';
import styled from '@xstyled/styled-components';

interface Props {
  current: number;
  total: number;
  /** Highest index the learner may jump to (visited cards). Defaults to all. */
  maxSelectable?: number;
  onSelect?: (index: number) => void;
}

/** Larger hit target than the 6px track (CAT-1435 / EL–E284). */
const SegmentButton = styled.button`
  flex: 1;
  min-width: 0;
  height: 24px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
    z-index: 1;
  }
`;

export const ProgressBar = ({
  current,
  total,
  maxSelectable = total - 1,
  onSelect
}: Props) => {
  const items: number[] = [];
  for (let i = 0; i < total; i++) items.push(i);

  return (
    <x.div
      role="group"
      aria-label={t`Pagination`}
      display="flex"
      h="24px"
      gap="sm"
      gridArea="progress"
      alignSelf="center"
      justifySelf="stretch"
      alignItems="center"
    >
      {items.map(i => {
        const page = i + 1;
        const isCurrent = i === current;
        const isDisabled = i > maxSelectable;
        return (
          <SegmentButton
            key={i}
            type="button"
            aria-label={t`Go to page ${page}`}
            aria-current={isCurrent ? 'true' : undefined}
            disabled={isDisabled}
            onClick={() => onSelect?.(i)}
          >
            <x.span
              display="block"
              w="100%"
              h="6px"
              borderRadius="3px"
              backgroundColor={i <= current ? '#FFF' : 'greyMed'}
              /* CAT-1441 / EL–E269: same greyDark stroke as course ProgressBar
                 (CAT-1239) — #a8a8a8 on light lime pages was only ~2.35:1. */
              border="1px solid"
              borderColor="greyDark"
              transition="all 0.4s ease-in-out"
              pointerEvents="none"
              aria-hidden="true"
            />
          </SegmentButton>
        );
      })}
    </x.div>
  );
};
